import * as XLSXStyle from 'xlsx-js-style';
import {
  applyGastosColumnLabels,
  buildTipoGastosLookup,
  buildGastosTipoSummaryRows,
  formatGastosCell,
  formatGastosPeriodoLegacy,
  groupGastosByTipo,
  mergeGastosEncabezado,
  resolveGastosGrandTotal,
  resolveGastosTipoGastoTexto,
  rowToGastosNorm,
  sumGastosImporteTotal,
  withGastosPeriodoLine,
} from './gastosReportModel';
import { appendFiltrosSucursalesCajasExcel } from './reporteFiltrosEncabezadoExport';

function appendGastosEncabezadoExcel(aoa, encMerged, form) {
  let titleRowIndex = -1;
  const up = (s) => String(s || '').trim().toUpperCase();
  if (encMerged.nombreComercial) {
    aoa.push([up(encMerged.nombreComercial)]);
  }
  if (encMerged.domicilioFiscal) {
    aoa.push([up(encMerged.domicilioFiscal)]);
  }
  if (encMerged.rfc) {
    aoa.push([up(encMerged.rfc)]);
  }
  if (encMerged.reporteTitulo) {
    titleRowIndex = aoa.length;
    aoa.push([String(encMerged.reporteTitulo).trim().toUpperCase()]);
  }
  aoa.push([
    encMerged.periodoTexto || formatGastosPeriodoLegacy(form.fechaInicio, form.fechaFin),
  ]);
  appendFiltrosSucursalesCajasExcel(aoa, encMerged, form);
  if (encMerged.turnosTexto) {
    aoa.push([`Turnos: ${encMerged.turnosTexto}`]);
  } else if (form.turnoInicio != null || form.turnoFin != null) {
    aoa.push([`Turnos: ${form.turnoInicio ?? '—'} a ${form.turnoFin ?? '—'}`]);
  }
  if (encMerged.tipoGastoTexto) {
    aoa.push([`Tipo de gasto: ${encMerged.tipoGastoTexto}`]);
  }
  aoa.push([]);
  return { titleRowIndex };
}

export function buildGastosExcelPayload(rawRows, options = {}) {
  const {
    fechaInicio = '',
    fechaFin = '',
    cajaInicio,
    cajaFin,
    turnoInicio,
    turnoFin,
    encabezado: encApi,
    encabezadoFallback,
    sucursalNombre = '',
    reporteTitulo,
    apiTotales = null,
    columnas = null,
    tiposCatalogo = [],
    tipoGastoFiltro = '',
    globalView = false,
  } = options;

  const form = { fechaInicio, fechaFin, cajaInicio, cajaFin, turnoInicio, turnoFin };
  const encBase = mergeGastosEncabezado(encApi, encabezadoFallback);
  const sn = String(sucursalNombre || '').trim();
  if (sn && !String(encBase.nombreComercial || '').toUpperCase().includes(sn.toUpperCase())) {
    encBase.nombreComercial = encBase.nombreComercial
      ? `${encBase.nombreComercial} - ${sn.toUpperCase()}`
      : sn.toUpperCase();
  }
  const encMerged = withGastosPeriodoLine(
    reporteTitulo ? { ...encBase, reporteTitulo } : encBase,
    fechaInicio,
    fechaFin,
    form
  );
  const tipoGastoTexto = resolveGastosTipoGastoTexto(
    tipoGastoFiltro,
    encMerged.tipoGastoTexto,
    tiposCatalogo,
  );
  if (tipoGastoTexto) encMerged.tipoGastoTexto = tipoGastoTexto;

  const aoa = [];
  const { titleRowIndex } = appendGastosEncabezadoExcel(aoa, encMerged, form);

  const list = Array.isArray(rawRows) ? rawRows : [];
  const resolved = applyGastosColumnLabels(columnas, globalView);
  const tipoLookup = buildTipoGastosLookup(tiposCatalogo);
  const normRows = list.map((r) => rowToGastosNorm(r, tipoLookup));
  const groups = groupGastosByTipo(normRows);
  const summaryRows = globalView ? buildGastosTipoSummaryRows(groups) : [];
  const grandTotal = resolveGastosGrandTotal(apiTotales, sumGastosImporteTotal(normRows));

  const encRows = aoa.length;
  aoa.push(resolved.map((c) => c.label));
  const colHeaderRow = encRows;
  const dataStartRow = encRows + 1;

  /** @type {Array<{ kind: 'group'|'row'|'subtotal'|'summary'|'total', groupIdx?: number }>} */
  const rowKinds = [];

  if (globalView) {
    for (let si = 0; si < summaryRows.length; si++) {
      const r = summaryRows[si];
      rowKinds.push({ kind: 'summary' });
      aoa.push(resolved.map((c) => formatGastosCell(c, r[c.id])));
    }
  } else {
    for (let gi = 0; gi < groups.length; gi++) {
      const g = groups[gi];
      rowKinds.push({ kind: 'group', groupIdx: gi });
      aoa.push([g.label, ...Array(Math.max(0, resolved.length - 1)).fill('')]);
      for (const r of g.rows) {
        rowKinds.push({ kind: 'row' });
        aoa.push(resolved.map((c) => formatGastosCell(c, r[c.id])));
      }
      rowKinds.push({ kind: 'subtotal', groupIdx: gi });
      aoa.push([
        ...Array(Math.max(0, resolved.length - 1)).fill(''),
        formatGastosCell({ kind: 'money' }, g.subtotal),
      ]);
    }
  }

  const totalRow = aoa.length;
  rowKinds.push({ kind: 'total' });
  aoa.push([
    'Total de gastos',
    ...Array(Math.max(0, resolved.length - 2)).fill(''),
    formatGastosCell({ kind: 'money' }, grandTotal),
  ]);

  return {
    aoa,
    resolved,
    encRows,
    colHeaderRow,
    dataStartRow,
    totalRow,
    titleRowIndex,
    rowKinds,
    groups,
  };
}

function styleGastosSheet(ws, meta) {
  const { resolved, encRows, colHeaderRow, dataStartRow, titleRowIndex, rowKinds } = meta;
  if (!resolved.length || colHeaderRow < 0) return;

  const nDataCol = resolved.length;
  if (!ws['!merges']) ws['!merges'] = [];

  const mergeRow = (r) => {
    if (nDataCol > 1) {
      ws['!merges'].push({ s: { r, c: 0 }, e: { r, c: nDataCol - 1 } });
    }
  };

  if (titleRowIndex >= 0) mergeRow(titleRowIndex);
  for (let r = 0; r <= encRows - 2; r++) {
    if (r === titleRowIndex) continue;
    mergeRow(r);
  }

  for (let r = 0; r <= encRows - 2; r++) {
    const addr = XLSXStyle.utils.encode_cell({ r, c: 0 });
    const cell = ws[addr];
    if (cell) {
      cell.s = {
        ...(cell.s || {}),
        alignment: { horizontal: 'center', wrapText: true },
        font: { ...((cell.s && cell.s.font) || {}), color: { rgb: 'FF0F172A' } },
      };
    }
  }

  if (titleRowIndex >= 0) {
    for (let c = 0; c < nDataCol; c++) {
      const addr = XLSXStyle.utils.encode_cell({ r: titleRowIndex, c });
      const cell = ws[addr];
      if (cell) {
        cell.s = {
          ...(cell.s || {}),
          font: { bold: true },
          alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
        };
      }
    }
  }

  for (let c = 0; c < nDataCol; c++) {
    const align = resolved[c].align === 'right' ? 'right' : 'left';
    const th = XLSXStyle.utils.encode_cell({ r: colHeaderRow, c });
    const tc = ws[th];
    if (tc) {
      tc.s = {
        ...(tc.s || {}),
        font: { bold: true, color: { rgb: 'FF000000' } },
        alignment: { horizontal: align },
        border: {
          top: { style: 'medium', color: { rgb: 'FF000000' } },
          bottom: { style: 'thin', color: { rgb: 'FF000000' } },
        },
      };
    }
  }

  let dataRow = dataStartRow;
  for (const kind of rowKinds) {
    if (kind.kind === 'group') {
      mergeRow(dataRow);
      const addr = XLSXStyle.utils.encode_cell({ r: dataRow, c: 0 });
      const cell = ws[addr];
      if (cell) {
        cell.s = {
          ...(cell.s || {}),
          font: { bold: true, color: { rgb: 'FF000000' } },
          alignment: { horizontal: 'left' },
        };
      }
    } else if (kind.kind === 'row' || kind.kind === 'summary') {
      for (let c = 0; c < nDataCol; c++) {
        const addr = XLSXStyle.utils.encode_cell({ r: dataRow, c });
        const cell = ws[addr];
        if (!cell) continue;
        cell.s = {
          ...(cell.s || {}),
          alignment: { horizontal: resolved[c].align === 'right' ? 'right' : 'left' },
        };
      }
    } else if (kind.kind === 'subtotal') {
      for (let c = 0; c < nDataCol; c++) {
        const addr = XLSXStyle.utils.encode_cell({ r: dataRow, c });
        const cell = ws[addr];
        if (!cell) continue;
        const isImporte = c === nDataCol - 1;
        cell.s = {
          ...(cell.s || {}),
          font: { bold: isImporte, color: { rgb: 'FF000000' } },
          alignment: { horizontal: resolved[c].align === 'right' ? 'right' : 'left' },
          border: isImporte
            ? { top: { style: 'double', color: { rgb: 'FF000000' } } }
            : undefined,
        };
      }
    } else if (kind.kind === 'total') {
      for (let c = 0; c < nDataCol; c++) {
        const addr = XLSXStyle.utils.encode_cell({ r: dataRow, c });
        const cell = ws[addr];
        if (!cell) continue;
        cell.s = {
          ...(cell.s || {}),
          font: { bold: true, color: { rgb: 'FF000000' } },
          alignment: { horizontal: c === 0 ? 'left' : resolved[c].align === 'right' ? 'right' : 'left' },
        };
      }
    }
    dataRow += 1;
  }
}

export function downloadGastosExcelFile(rawRows, options, fileName, sheetName) {
  const payload = buildGastosExcelPayload(rawRows, options);
  const { aoa, resolved, encRows, colHeaderRow, dataStartRow, totalRow, titleRowIndex, rowKinds, groups } =
    payload;

  const ws = XLSXStyle.utils.aoa_to_sheet(aoa);
  if (resolved.length) {
    styleGastosSheet(ws, {
      resolved,
      encRows,
      colHeaderRow,
      dataStartRow,
      totalRow,
      titleRowIndex,
      rowKinds,
      groups,
    });
  }

  const colCount = Math.max(1, aoa.reduce((m, row) => Math.max(m, row.length), 0));
  ws['!cols'] = Array.from({ length: colCount }, (_, i) => ({
    wch: [20, 10, 40, 14][i] ?? 12,
  }));

  const wb = XLSXStyle.utils.book_new();
  XLSXStyle.utils.book_append_sheet(wb, ws, sheetName);
  XLSXStyle.writeFile(wb, fileName);
}
